import { Suspense, useCallback, useMemo, useState } from 'react';
import { t } from '@lingui/core/macro';

import { graphql, useFragment } from 'react-relay';

import { ProjectState } from '@watershed/constants/projects';
import SetSquareIcon from '@watershed/icons/components/SetSquare';
import { type SupportedLocale } from '@watershed/intl/constants';
import useLocale from '@watershed/intl/frontend/useLocale';
import {
  relayDateStringToJsDate,
  relayDateTimeStringToJsDate,
} from '@watershed/shared-frontend/relay/scalars';
import { ProjectActionForRelay } from '@watershed/shared-universal/projects/Project';
import DateTimeUtils from '@watershed/shared-universal/utils/DateTimeUtils';
import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import BlankSlate from '@watershed/ui-core/components/BlankSlate';
import {
  GridColDef,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from '@watershed/ui-core/components/DataGrid/DataGrid';
import DatePill from '@watershed/ui-core/components/DatePill';
import { ObjectList } from '@watershed/ui-core/components/ObjectList/ObjectList';
import { ObjectListToolbarQuickSort } from '@watershed/ui-core/components/ObjectList/ObjectListToolbarQuickSort';
import Pill from '@watershed/ui-core/components/Pill';
import Skeleton from '@watershed/ui-core/components/Skeleton';
import TruncatingTypographyWithTooltip from '@watershed/ui-core/components/TruncatingTypographyWithTooltip';

import FlexibleProjectPageActions from './FlexibleProjectPageActions';
import {
  type FlexibleProjectsList_projects$data,
  type FlexibleProjectsList_projects$key,
} from './__generated__/FlexibleProjectsList_projects.graphql';
import FlexibleProjectActionDialog from './FlexibleProjectActionDialog';
import FlexibleProjectProgress from './FlexibleProjectProgress';
import FlexibleProjectRowActions from './FlexibleProjectRowActions';
import { formatNullableDateRangeRelay } from './utils/relayProjectUtils';

// NOTE: do not export this type; only used for local readability
type FlexibleProjectRow = FlexibleProjectsList_projects$data[number];

// Height offsets for calculating object list height
const PAGE_HEADER_HEIGHT = 64;
const HEADER_BORDER_HEIGHT = 1;
const CONTENT_PADDING = 32;
const TOTAL_HEADER_OFFSET =
  PAGE_HEADER_HEIGHT + HEADER_BORDER_HEIGHT + CONTENT_PADDING;

const FlexibleProjectsListFragmentGql = graphql`
  fragment FlexibleProjectsList_projects on Project @relay(plural: true) {
    # TODO: Fix these existing violations
    # eslint-disable-next-line relay/unused-fields
    id
    # eslint-disable-next-line relay/unused-fields
    name
    state
    url
    dueDate
    dateRange {
      start
      end
    }
    keyEvents {
      created {
        dateTime
        actor {
          # TODO: Fix these existing violations
          # eslint-disable-next-line relay/unused-fields
          id
          displayName
        }
      }
    }
    ...FlexibleProjectProgress_project
      @defer(label: "FlexibleProjectProgressProject")
    ...FlexibleProjectRowActions_project
      @defer(label: "FlexibleProjectRowActionsProject")
  }
`;

export default function FlexibleProjectsList({
  projectsRef,
}: {
  projectsRef: FlexibleProjectsList_projects$key;
}) {
  const locale = useLocale();
  const projects = useFragment(FlexibleProjectsListFragmentGql, projectsRef);

  // Register state -----
  const [selectedAction, setSelectedAction] =
    useState<ProjectActionForRelay | null>(null);
  const onCloseDialog = useCallback(() => {
    setSelectedAction(null);
  }, []);

  const columns = useMemo(
    () => getColumns(locale, setSelectedAction),
    [locale, setSelectedAction]
  );

  return (
    <>
      <ObjectList<FlexibleProjectRow>
        data-testid={TestIds.FlexibleProjectsTable}
        rows={projects}
        columns={columns}
        initialState={{
          sorting: {
            // Defaults to sorting by active at top, soonest due date first
            sortModel: [
              { field: 'state', sort: 'asc' },
              { field: 'dueDate', sort: 'asc' },
            ],
          },
        }}
        sx={{
          maxHeight: `calc(100svb - ${TOTAL_HEADER_OFFSET}px)`,
          minHeight: projects.length > 0 ? undefined : 360, // min height to make space for the blank slate
        }}
        autoHeight={projects.length === 0} // This removes the scrollbar that shows up for the blank slate
        slots={{
          toolbar: projects.length > 0 ? ProjectsToolbar : undefined,
          noRowsOverlay: () => <ProjectsBlankSlate />,
        }}
      />
      {selectedAction && (
        <FlexibleProjectActionDialog
          selectedAction={selectedAction}
          onCloseDialog={onCloseDialog}
        />
      )}
    </>
  );
}

function ProjectsToolbar() {
  return (
    <GridToolbarContainer
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        padding: 0,
      }}
    >
      <GridToolbarQuickFilter />
      <ObjectListToolbarQuickSort excludeColumns={['progress']} />
    </GridToolbarContainer>
  );
}

function ProjectsBlankSlate() {
  return (
    <BlankSlate
      data-testid={TestIds.FlexibleProjectsTableBlankSlate}
      icon={SetSquareIcon}
      title={t({
        message: 'Start measuring',
        context: 'Header if no projects',
      })}
      subtitle={t({
        message:
          'Collect business activity from across your organization to use in footprints.',
        context: 'Subtitle if no projects',
      })}
      sx={{ maxWidth: 512, marginInline: 'auto' }}
    >
      <FlexibleProjectPageActions />
    </BlankSlate>
  );
}

function getColumns(
  locale: SupportedLocale,
  setSelectedAction: (action: ProjectActionForRelay | null) => void
): Array<GridColDef<FlexibleProjectRow>> {
  const columns: Array<GridColDef<FlexibleProjectRow>> = [
    {
      field: 'name',
      headerName: t({
        message: 'Project',
        context: 'Column header for project name',
      }),
      flex: 2,
      renderCell: ({ value, row }) => {
        // TODO: verify in prod the dates look correct
        const { dateRange } = row;
        const dateRangeSubtitle = formatNullableDateRangeRelay({
          locale,
          start: dateRange?.start,
          end: dateRange?.end,
        });
        return (
          <ObjectList.TitleCell
            data-testid={TestIds.FlexibleProjectNameCell}
            href={row.url}
            title={value}
            subtitle={dateRangeSubtitle}
          />
        );
      },
    },
    {
      field: 'state',
      headerName: t({
        message: 'Status',
        context: 'Column header for project status',
      }),
      flex: 1,
      renderCell: ({ row }) =>
        row.state === ProjectState.Active ? (
          <Pill
            color="primary"
            label={t({
              message: 'Active',
              context: 'Status of a project',
            })}
          />
        ) : (
          <Pill
            color="default"
            variant="outlined"
            label={t({
              message: 'Closed',
              context: 'Status of a project',
            })}
          />
        ),
      // Not searchable by status
      getApplyQuickFilterFn: undefined,
    },
    {
      field: 'progress',
      headerName: t({
        message: 'Progress',
        context: 'Column header for project progress',
      }),
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      type: 'number',
      // Not searchable by progress
      getApplyQuickFilterFn: undefined,
      renderCell: ({ row }) => {
        return (
          <Suspense fallback={<CellSuspenseLoading />}>
            <FlexibleProjectProgress projectRef={row} />
          </Suspense>
        );
      },
    },
    {
      field: 'dueDate',
      headerName: t({
        message: 'Due date',
        context: 'Column header for the date a project is due',
      }),
      flex: 1,
      type: 'date',
      renderCell: ({ row }) => {
        if (!row.dueDate) return null;
        const dueDate = relayDateStringToJsDate(row.dueDate);
        return (
          <TruncatingTypographyWithTooltip
            variant="body2"
            tooltip={DateTimeUtils.formatDate(
              DateTimeUtils.fromJSDate(dueDate),
              { format: 'med', locale }
            )}
          />
        );
      },
      valueGetter: ({ row }) =>
        row.dueDate ? relayDateStringToJsDate(row.dueDate) : null,
      // Do not apply quick filter to dates
      getApplyQuickFilterFn: undefined,
    },
    {
      field: 'createdAt',
      headerName: t({
        message: 'Created',
        context: 'Column header for the date a project was created',
      }),
      type: 'date',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: ({ row }) => {
        const createdEvent = row.keyEvents.created;
        const createdDateTime = relayDateTimeStringToJsDate(
          createdEvent.dateTime
        );
        return (
          <DatePill
            date={createdDateTime}
            dateEventType="created"
            showTooltip
            userName={createdEvent.actor?.displayName}
          />
        );
      },
      valueGetter: ({ row }) =>
        relayDateTimeStringToJsDate(row.keyEvents.created.dateTime),
      // Do not apply quick filter to dates
      getApplyQuickFilterFn: undefined,
    },
    {
      field: 'menu',
      type: 'actions' as const,
      headerName: '',
      renderCell: (params) => (
        // The data to show whether we can close/reopen etc. is heavy and
        // thus deferred so show loading to not block the table
        <Suspense fallback={<CellSuspenseLoading />}>
          <FlexibleProjectRowActions
            projectRef={params.row}
            setSelectedAction={setSelectedAction}
          />
        </Suspense>
      ),
      // Do not apply quick filter to menu
      getApplyQuickFilterFn: undefined,
      width: 56,
    },
  ];

  return columns;
}

function CellSuspenseLoading() {
  return <Skeleton variant="circular" width={20} height={20} />;
}
